import { useState, useEffect, useContext, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ViewContext } from 'components/lib';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowRight from '@untitled-ui/icons-react/build/cjs/ArrowRight';
import { UMButton } from '@urbanmetrix/um-kit';
import ExitDialog from 'components/exit-dialog/ExitDialog';
import { ValidationContext } from 'contexts/ValidationContext';
import Cadastral from './cadastral/Cadastral';
import GridSettingsForm from './GridSettingsForm';
import CadastralMapSection, { ReadOnlyAnalysisAreaCenterMarkerSync } from './map/CadastralMapSection';
import MapPreviewModeFrame from './map/MapPreviewModeFrame';
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { GridContext } from 'contexts/GridContext';
import { GeodataContext } from 'contexts/GeodataContext';
import ReviewHeader from '../project-page/components/ReviewHeader';
import { UMLoader } from '@urbanmetrix/um-kit';
import {
  addGrid,
  calculateGridVolume,
  getGrid,
  getGridSensorPoints,
  getGridSize,
  replaceGrid,
  updateGrid,
} from '../../api/gridApi';
import { getProjectWithChildren, updateProject } from '../../api/projectApi';
import {
  defaultCoordinateSystemPreference,
  normalizeProjectCoordinatesForMap,
} from '../../utils/coordinateSystemManager';
import { formatBytesToMbLabel } from '../../utils/formatBytes';
import L from 'leaflet';
import Style from './styles/grid.tailwind';
import Settings from 'settings.json';
import { crs2056, INITIAL_GRID_MAP_VIEWS, type GridMapType } from './map/mapConfig';
import { buildGridParamsPayload, type RawGridParams } from './gridPayload';
import { enrichGridParamsPayloadWithHeight } from 'services/gridHeightEnrichment';
import { checkSensorPoints, checkVolume } from 'services/gridLimitChecks';
import { normalizeMultiPolygon } from 'utils/multiPolygonHelper';
import type { GridInsert } from '@urbanmetrix/sdk';
import ProjectCreationShell from 'views/project-creation/ProjectCreationShell';
import ProjectCreationSplitLayout from 'views/project-creation/ProjectCreationSplitLayout';
import { buildGridGeodataPayload, buildGridGeodataReplacePayload } from 'views/add-geodata/persistDraftGeodata';
import type { ProjectCreationPreviewState } from 'views/project-creation/ProjectCreationPersistentPreview';
import usePreviewSlotRectReporter from 'views/project-creation/usePreviewSlotRectReporter';
import { ABSOLUTE_FLAT_TERRAIN_HEIGHT_MAX, ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN } from 'constants/grid';
import { isInputNumberInRange } from 'utils/inputNumberErrorMessage';
import { getApiErrorMessage } from 'api/apiClient';
import { getCurrentUsage } from 'api/planApi';
import { getGeodataModuleLimitMessage, getGeodataRequiredModuleCount } from 'services/geodataUsage';
import { getGridResolutionWithinLimit } from 'services/gridResolution';
import { getProjectSpecDefaults } from 'services/projectSpecs';
interface GridProjectData {
  id?: string | number;
  paidVolume?: number | string | null;
  geodatasAvailableCount?: number | null;
  projectCoordinates?: unknown;
  coordinates?: { lat: number; lng: number } | null;
  grids?: Array<{ id?: string | number }>;
  grossFloorSurface?: number | null;
  numberOfFloors?: number | null;
  [key: string]: unknown;
}

interface AddGridProps {
  reviewMode?: boolean;
  reviewGridId?: string | null;
  breadcrumbTitle?: ReactNode;
  projectIdOverride?: string | null;
  gridIdOverride?: string | null;
  draftGeodataId?: string | null;
  disableAdvanceActions?: boolean;
  onOpenGeodataSettings?: (gridId: string) => void | Promise<void>;
  onOpenSimulationSettings?: (gridId: string) => void | Promise<void>;
  onPersistDraftGrid?: (gridId: string, gridResolution?: number) => Promise<PersistedGridWithGeodataSummary | void>;
  onPersistDraftGridForSimulation?: (gridId: string) => Promise<void>;
  onUpdatePreview?: (gridId: string) => void | Promise<void>;
  onDraftResourcesChange?: (ids: {
    gridId: string;
    geodataId: string | null;
    progressStatus: string | null;
  }) => void | Promise<void>;
  onExitOverride?: () => void;
  onPreviewStateChange?: (preview: ProjectCreationPreviewState | null) => void;
  onPreviewSlotRectChange?: (rect: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'> | null) => void;
  previewRectAnchor?: HTMLElement | null;
}

type PersistedGridWithGeodataSummary = {
  id?: string | number;
  geodataId?: string | number | null;
  geodatas?: Array<{ id?: string | number }>;
  progressStatus?: string | null;
};

const AddGrid = ({
  reviewMode = false,
  reviewGridId = null,
  breadcrumbTitle,
  projectIdOverride = null,
  gridIdOverride = null,
  draftGeodataId = null,
  disableAdvanceActions = false,
  onOpenGeodataSettings,
  onOpenSimulationSettings,
  onPersistDraftGrid,
  onPersistDraftGridForSimulation,
  onUpdatePreview,
  onDraftResourcesChange,
  onExitOverride,
  onPreviewStateChange,
  onPreviewSlotRectChange,
  previewRectAnchor = null,
}: AddGridProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const viewContext = useContext(ViewContext);
  const params = useParams();
  const projectId = projectIdOverride ?? params.projectId ?? null;
  const routeGridId = gridIdOverride ?? params.gridId ?? null;
  const startsAtAnalysisVolume = new URLSearchParams(location.search).get('gridStep') === 'analysis-volume';
  const resolvedReviewGridId = reviewGridId ?? null;
  const isReadOnlyReview = Boolean(reviewMode || resolvedReviewGridId);

  const {
    grid,
    gridParams,
    setGrid,
    setGridParams,
    cloneGridData,
    setIsCloningGrid,
    sensorPointLimit,
    setSensorPointLimit,
    setVolumeLimit,
    volumeLimit,
    gridVolume,
    setGridVolume,
    sensorPoints,
    setSensorPoints,
  } = useContext(GridContext);
  const { geodata, geodataParams } = useContext(GeodataContext);
  const { currentProject } = useContext(CurrentProjectContext);
  const { isGridAnalysisAreaValid, isGridCoordinateSystemValid, isGridNameValid } = useContext(ValidationContext);

  const [resolvedProjectId, setResolvedProjectId] = useState(projectId ?? null);
  const [draftGridId, setDraftGridId] = useState<string | null>(routeGridId ?? null);
  const [projectData, setProjectData] = useState<GridProjectData | null>(null);
  const [gridSizeLabel, setGridSizeLabel] = useState<string | null>(null);
  const [gridSettingsMapType, setGridSettingsMapType] = useState<GridMapType>('cadastral');
  const [gridSettingsMapViews, setGridSettingsMapViews] = useState(INITIAL_GRID_MAP_VIEWS);
  const [activeStep, setActiveStep] = useState(() =>
    gridIdOverride && !reviewMode && !reviewGridId && !startsAtAnalysisVolume ? 1 : 0
  );
  const [isOpeningSimulationSettings, setIsOpeningSimulationSettings] = useState(false);
  const [isCreatingStandaloneGrid, setIsCreatingStandaloneGrid] = useState(false);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [isGridVolumeLoading, setIsGridVolumeLoading] = useState(false);
  const [hasGridVolumeCalculationError, setHasGridVolumeCalculationError] = useState(false);
  const [isSensorPointsLoading, setIsSensorPointsLoading] = useState(false);
  const [hasSensorPointsCalculationError, setHasSensorPointsCalculationError] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [pendingNavigateTo, setPendingNavigateTo] = useState<string | null>(null);
  const legalBorderRef = useRef<L.Polygon | null>(null);
  const buildableBorderRef = useRef<L.Polygon | null>(null);
  const analysisAreaRef = useRef<L.Polygon | null>(null);
  const legalBorderInactiveRefs = useRef<L.Polygon[]>([]);
  const buildableBorderInactiveRefs = useRef<L.Polygon[]>([]);
  const analysisAreaInactiveRefs = useRef<L.Polygon[]>([]);
  const isDrawingRef = useRef(false);
  const isGridResolutionUserDefinedRef = useRef(Boolean(routeGridId));
  const previewSlotRef = useRef<HTMLDivElement | null>(null);
  const usesHashNavigation = !projectIdOverride && !gridIdOverride && !reviewMode;
  const isProjectCreationFlow = typeof onDraftResourcesChange === 'function';
  const usesExternalPreview = isProjectCreationFlow && typeof onPreviewStateChange === 'function';
  const isStandaloneGridCreationFlow =
    !isReadOnlyReview &&
    !projectIdOverride &&
    !gridIdOverride &&
    typeof onOpenGeodataSettings !== 'function' &&
    typeof onOpenSimulationSettings !== 'function';

  useEffect(() => {
    if (!usesHashNavigation) {
      return;
    }

    const syncStepFromHash = () => {
      setActiveStep(window.location.hash === '#2' ? 1 : 0);
    };

    syncStepFromHash();
    window.addEventListener('hashchange', syncStepFromHash);

    return () => {
      window.removeEventListener('hashchange', syncStepFromHash);
    };
  }, [usesHashNavigation]);

  useEffect(() => {
    if (!usesHashNavigation) {
      return;
    }

    const nextHash = `#${activeStep + 1}`;
    if (window.location.hash !== nextHash) {
      window.location.hash = nextHash;
    }
  }, [activeStep, usesHashNavigation]);

  useEffect(() => {
    if (!isReadOnlyReview) {
      setResolvedProjectId(projectId ?? null);
      return;
    }
    if (!resolvedReviewGridId) return;

    let isMounted = true;

    const fetchReviewGrid = async () => {
      try {
        const gridData = await getGrid(String(resolvedReviewGridId));
        if (!isMounted || !gridData) return;

        cloneGridData(gridData, { appendClonedSuffix: false });
        setIsCloningGrid(true);
        setDraftGridId(String(gridData.id));
        setResolvedProjectId(gridData.projectId ?? null);
      } catch (error) {
        console.error('Error fetching review grid:', error);
      }
    };

    fetchReviewGrid();

    return () => {
      isMounted = false;
    };
  }, [cloneGridData, isReadOnlyReview, projectId, resolvedReviewGridId, setIsCloningGrid]);

  useEffect(() => {
    if (isReadOnlyReview || !routeGridId) {
      return;
    }

    if (isProjectCreationFlow) {
      setDraftGridId(String(routeGridId));
      setResolvedProjectId(projectId ?? null);
      return;
    }

    let isMounted = true;

    const loadDraftGrid = async () => {
      try {
        const gridData = await getGrid(String(routeGridId));
        if (!isMounted || !gridData) return;

        cloneGridData(gridData, { appendClonedSuffix: false });
        setDraftGridId(String(gridData.id));
        setResolvedProjectId(gridData.projectId ?? projectId ?? null);
      } catch (error) {
        console.error('Error fetching draft grid:', error);
      }
    };

    loadDraftGrid();

    return () => {
      isMounted = false;
    };
  }, [cloneGridData, isProjectCreationFlow, isReadOnlyReview, projectId, routeGridId]);

  useEffect(() => {
    setSensorPointLimit(null);
    setVolumeLimit(null);
  }, [setSensorPointLimit, setVolumeLimit]);

  useEffect(() => {
    let active = true;
    getCurrentUsage()
      .then((usage) => {
        if (!active) return;
        const parsedVolumeLimit = Number(usage?.volumeLimit);
        const parsedSensorPointLimit = Number(usage?.sensorPointsLimit);
        setVolumeLimit(Number.isFinite(parsedVolumeLimit) && parsedVolumeLimit > 0 ? parsedVolumeLimit : null);
        setSensorPointLimit(
          Number.isFinite(parsedSensorPointLimit) && parsedSensorPointLimit > 0 ? parsedSensorPointLimit : null
        );
      })
      .catch(() => {
        if (!active) return;
        setVolumeLimit(null);
        setSensorPointLimit(null);
      });
    return () => {
      active = false;
    };
  }, [setSensorPointLimit, setVolumeLimit]);

  useEffect(() => {
    let isMounted = true;

    if (isProjectCreationFlow) {
      setProjectData(currentProject ? (currentProject as GridProjectData) : null);
      return () => {
        isMounted = false;
      };
    }

    const matchingCurrentProject =
      currentProject && resolvedProjectId && String(currentProject.id) === String(resolvedProjectId)
        ? currentProject
        : null;

    if (matchingCurrentProject) {
      setProjectData(matchingCurrentProject as GridProjectData);
      return () => {
        isMounted = false;
      };
    }

    if (!resolvedProjectId) {
      setProjectData(null);
      return () => {
        isMounted = false;
      };
    }

    const loadProject = async () => {
      try {
        const rawProject = await getProjectWithChildren(String(resolvedProjectId));
        if (!isMounted || !rawProject) return;

        setProjectData(
          normalizeProjectCoordinatesForMap({
            ...rawProject,
            coordinates: rawProject.projectCoordinates ?? undefined,
          }) as GridProjectData | null
        );
      } catch (error) {
        console.error('Error fetching project for grid creation:', error);
      }
    };

    loadProject();

    return () => {
      isMounted = false;
    };
  }, [currentProject, isProjectCreationFlow, resolvedProjectId]);

  useEffect(() => {
    if (!isReadOnlyReview || !resolvedReviewGridId) {
      setGridSizeLabel(null);
      return;
    }

    let isMounted = true;

    const loadGridSize = async () => {
      try {
        const sizePayload = await getGridSize(String(resolvedReviewGridId));
        if (!isMounted) return;

        const gridSize = sizePayload?.grids?.find((item) => String(item.id) === String(resolvedReviewGridId))?.size;
        setGridSizeLabel(formatBytesToMbLabel(gridSize) ?? '-');
      } catch (error) {
        if (!isMounted) return;
        console.error('Error fetching review grid size:', error);
        setGridSizeLabel('-');
      }
    };

    loadGridSize();

    return () => {
      isMounted = false;
    };
  }, [isReadOnlyReview, resolvedReviewGridId]);

  const exitTarget = useMemo(() => {
    if (!resolvedProjectId) return null;
    return `/projects/${resolvedProjectId}`;
  }, [resolvedProjectId]);
  const gridSettingsMapCenter = useMemo<[number, number]>(() => {
    if (projectData?.coordinates?.lat != null && projectData?.coordinates?.lng != null) {
      return [projectData.coordinates.lat, projectData.coordinates.lng];
    }
    return [0, 0];
  }, [projectData?.coordinates?.lat, projectData?.coordinates?.lng]);
  const defaultGridName = useMemo(() => {
    const gridCount = Array.isArray(projectData?.grids) ? projectData.grids.length : 0;
    return `Grid ${gridCount + 1}`;
  }, [projectData?.grids]);

  useEffect(() => {
    if (gridSettingsMapCenter[0] === 0 || gridSettingsMapCenter[1] === 0) {
      return;
    }

    setGridSettingsMapViews((prev) => ({
      cadastral: { ...prev.cadastral, center: gridSettingsMapCenter },
      osm: { ...prev.osm, center: gridSettingsMapCenter },
    }));
  }, [gridSettingsMapCenter]);

  usePreviewSlotRectReporter({
    enabled: usesExternalPreview && activeStep === 1,
    element: previewSlotRef.current,
    relativeTo: previewRectAnchor,
    onRectChange: onPreviewSlotRectChange,
  });

  const handleExit = () => {
    if (typeof onExitOverride === 'function') {
      onExitOverride();
      return;
    }

    if (exitTarget) {
      navigate(exitTarget);
      return;
    }
    navigate(-1);
  };

  const handleCloseClick = () => {
    setPendingNavigateTo(null);
    if (isReadOnlyReview) {
      handleExit();
      return;
    }

    setExitModalOpen(true);
  };

  const handleBillingLinkClick = () => {
    setPendingNavigateTo('/account/subscription');
    setExitModalOpen(true);
  };

  const persistDraftGrid = async (gridResolution: number | string = gridParams.gridResolution) => {
    if (!resolvedProjectId) {
      throw new Error('Missing project id');
    }

    if (!draftGridId && isProjectCreationFlow) {
      const parsedRemainingGeodatas = Number(projectData?.geodatasAvailableCount);
      const remainingGeodatas = Number.isFinite(parsedRemainingGeodatas) ? parsedRemainingGeodatas : null;
      const geodataLimitMessage = getGeodataModuleLimitMessage(
        remainingGeodatas,
        getGeodataRequiredModuleCount(geodata)
      );
      if (geodataLimitMessage) throw new Error(geodataLimitMessage);
    }

    const nextGridParams = { ...gridParams, gridResolution };
    const params = (await enrichGridParamsPayloadWithHeight(
      buildGridParamsPayload(nextGridParams as RawGridParams)
    )) as NonNullable<GridInsert['params']>;
    const payload = {
      name: draftGridId ? grid.name : grid.name || defaultGridName,
      description: grid.description || null,
      params,
    };

    const geodataPayload = buildGridGeodataPayload(geodata, geodataParams);
    const persistedGrid = draftGridId
      ? draftGeodataId
        ? await replaceGrid(String(draftGridId), {
            ...payload,
            geodata: buildGridGeodataReplacePayload(geodata, geodataParams),
          })
        : await updateGrid(String(draftGridId), payload)
      : await addGrid({
          projectId: String(resolvedProjectId),
          ...payload,
          ...(isProjectCreationFlow ? { geodata: geodataPayload } : {}),
        });

    setDraftGridId(String(persistedGrid.id));
    return persistedGrid;
  };

  const getPersistedGeodataId = (persistedGrid: PersistedGridWithGeodataSummary | void | null | undefined) => {
    const persistedGeodataId = persistedGrid?.geodataId ?? persistedGrid?.geodatas?.[0]?.id;
    return persistedGeodataId == null ? null : String(persistedGeodataId);
  };

  const handleNextStep = async () => {
    if (isAdvancingStep || disableAdvanceActions) {
      return;
    }

    if (isReadOnlyReview) {
      setActiveStep(1);
      return;
    }

    try {
      setIsAdvancingStep(true);
      const currentGridResolution = Number(gridParams.gridResolution);
      const normalizedCurrentResolution =
        Number.isFinite(currentGridResolution) && currentGridResolution > 0 ? currentGridResolution : 1;
      const resolutionRequiredByLimit = getGridResolutionWithinLimit({
        gridVolume,
        sensorPointLimit,
        volumeLimit: numericVolumeLimit,
        currentResolution: normalizedCurrentResolution,
        isResolutionUserDefined: true,
      });
      const gridResolution = getGridResolutionWithinLimit({
        gridVolume,
        sensorPointLimit,
        volumeLimit: numericVolumeLimit,
        currentResolution: normalizedCurrentResolution,
        isResolutionUserDefined: isGridResolutionUserDefinedRef.current,
      });
      const wasResolutionAdjustedByLimit =
        resolutionRequiredByLimit > normalizedCurrentResolution && resolutionRequiredByLimit === gridResolution;
      const gridHeight = Number(gridParams.gridHeight);
      const legalBorders = normalizeMultiPolygon<{ x: number; y: number }>(
        gridParams.legalBorders?.length ? gridParams.legalBorders : gridParams.legalBorder
      ).filter((polygon) => polygon.length >= 3);
      const defaults = getProjectSpecDefaults(gridHeight, legalBorders);
      const projectSpecUpdates: { grossFloorSurface?: number; numberOfFloors?: number } = {};
      if (projectData?.numberOfFloors == null) {
        projectSpecUpdates.numberOfFloors = defaults.numberOfFloors;
      }
      if (projectData?.grossFloorSurface == null && defaults.grossFloorSurface != null) {
        projectSpecUpdates.grossFloorSurface = defaults.grossFloorSurface;
      }
      if (resolvedProjectId && Object.keys(projectSpecUpdates).length > 0) {
        await updateProject(String(resolvedProjectId), projectSpecUpdates);
        setProjectData((current) => (current ? { ...current, ...projectSpecUpdates } : current));
      }

      const existingGridId = draftGridId ? String(draftGridId) : null;
      const persistedGrid =
        existingGridId && typeof onPersistDraftGrid === 'function'
          ? await onPersistDraftGrid(existingGridId, gridResolution)
          : await persistDraftGrid(gridResolution);
      const persistedGridId = existingGridId ?? String(persistedGrid?.id);
      setGridParams({ gridResolution });
      setDraftGridId(persistedGridId);

      if (wasResolutionAdjustedByLimit) {
        viewContext.notification.show(
          `Grid resolution was automatically set to ${gridResolution} m to stay within your plan's sensor point limit.`,
          'warning',
          true,
          'toast'
        );
      }

      if (typeof onDraftResourcesChange === 'function') {
        await onDraftResourcesChange({
          gridId: persistedGridId,
          geodataId: getPersistedGeodataId(persistedGrid as PersistedGridWithGeodataSummary) ?? draftGeodataId ?? null,
          progressStatus: persistedGrid?.progressStatus ?? null,
        });
      }

      setActiveStep(1);
    } catch (error) {
      console.error('Error preparing draft grid before moving to Grid Settings:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to prepare the grid with a valid sensor point resolution.'),
        'error',
        true,
        'toast'
      );
    } finally {
      setIsAdvancingStep(false);
    }
  };

  const handleOpenGeodataSettings = async () => {
    if (isReadOnlyReview || hasGridSensorPointsError || hasGridInputValidationError) {
      return;
    }

    try {
      if (typeof onDraftResourcesChange === 'function' && draftGridId) {
        if (typeof onOpenGeodataSettings === 'function') {
          await onOpenGeodataSettings(String(draftGridId));
          return;
        }
        navigate(`/geodatas/add/${draftGridId}`);
        return;
      }

      const persistedGrid = await persistDraftGrid();
      if (typeof onOpenGeodataSettings === 'function') {
        await onOpenGeodataSettings(String(persistedGrid.id));
        return;
      }
      navigate(`/geodatas/add/${persistedGrid.id}`);
    } catch (error) {
      console.error('Error saving draft grid before opening Geodata settings:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to save grid changes right now.'),
        'error',
        true,
        'toast'
      );
    }
  };

  const handleOpenSimulationSettings = async () => {
    if (
      isReadOnlyReview ||
      isOpeningSimulationSettings ||
      disableAdvanceActions ||
      hasGridSensorPointsError ||
      hasGridInputValidationError
    ) {
      return;
    }

    try {
      setIsOpeningSimulationSettings(true);
      if (typeof onDraftResourcesChange === 'function' && draftGridId) {
        const persistDraftGridForSimulation = onPersistDraftGridForSimulation ?? onPersistDraftGrid;
        if (typeof persistDraftGridForSimulation === 'function') {
          await persistDraftGridForSimulation(String(draftGridId));
        }
        if (typeof onOpenSimulationSettings === 'function') {
          await onOpenSimulationSettings(String(draftGridId));
          return;
        }
      }

      const persistedGrid = await persistDraftGrid();
      if (typeof onOpenSimulationSettings === 'function') {
        await onOpenSimulationSettings(String(persistedGrid.id));
        return;
      }
    } catch (error) {
      console.error('Error saving draft grid before opening Simulation settings:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to save grid changes right now.'),
        'error',
        true,
        'toast'
      );
    } finally {
      setIsOpeningSimulationSettings(false);
    }
  };

  const handleCreateStandaloneGrid = async () => {
    if (isReadOnlyReview || isCreatingStandaloneGrid || hasGridSensorPointsError || hasGridInputValidationError) {
      return;
    }

    try {
      setIsCreatingStandaloneGrid(true);
      await persistDraftGrid();
      navigate(`/projects/${resolvedProjectId}`);
    } catch (error) {
      console.error('Error creating grid from standalone grid flow:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to create grid right now.'),
        'error',
        true,
        'toast'
      );
      setIsCreatingStandaloneGrid(false);
    }
  };

  const sensorPointsGridId = draftGridId ?? routeGridId;
  useEffect(() => {
    const gridResolution = Number(gridParams.gridResolution);
    if (activeStep !== 1 || !sensorPointsGridId || !Number.isFinite(gridResolution) || gridResolution <= 0) {
      setSensorPoints({ totalSensorPoints: null, message: '', type: '', showBillingLink: false });
      setIsSensorPointsLoading(false);
      setHasSensorPointsCalculationError(false);
      return;
    }

    let cancelled = false;
    setIsSensorPointsLoading(true);
    setHasSensorPointsCalculationError(false);
    setSensorPoints({ totalSensorPoints: null, message: '', type: '', showBillingLink: false });

    getGridSensorPoints(String(sensorPointsGridId), gridResolution)
      .then(({ sensorPoints: calculatedSensorPoints }) => {
        if (cancelled) return;
        const sensorPointsValue = Number(calculatedSensorPoints);
        if (!Number.isFinite(sensorPointsValue) || sensorPointsValue < 0) {
          throw new Error('Invalid sensor points response');
        }
        const checkResult = checkSensorPoints({
          totalSensorPoints: sensorPointsValue,
          sensorPointLimit,
          locale: 'en-CH',
        });
        setSensorPoints({
          totalSensorPoints: sensorPointsValue,
          message: checkResult.message,
          type: checkResult.type,
          showBillingLink: checkResult.showBillingLink,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading grid sensor points:', error);
        setHasSensorPointsCalculationError(true);
        setSensorPoints({
          totalSensorPoints: null,
          message: 'Unable to calculate sensor points.',
          type: 'error',
          showBillingLink: false,
        });
        viewContext.notification.show(
          'We could not calculate the sensor points for this grid resolution. Please review the resolution and try again.',
          'error',
          true,
          'toast'
        );
      })
      .finally(() => {
        if (!cancelled) setIsSensorPointsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeStep, gridParams.gridResolution, sensorPointLimit, sensorPointsGridId, setSensorPoints]);

  const hasGridSensorPointsError =
    sensorPoints?.type === 'error' ||
    (activeStep === 1 &&
      !hasSensorPointsCalculationError &&
      (isSensorPointsLoading || sensorPoints.totalSensorPoints == null));
  const hasGridInputValidationError = useMemo(
    () =>
      !isInputNumberInRange(gridParams.gridResolution, 1, 20) ||
      !isInputNumberInRange(gridParams.gridHeight, 1, 300) ||
      (gridParams.flatTerrainMode === 'Absolute' &&
        !isInputNumberInRange(
          gridParams.flatTerrainHeight,
          ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN,
          ABSOLUTE_FLAT_TERRAIN_HEIGHT_MAX
        )) ||
      (Number(gridParams.gridAngle) !== 0 && !isInputNumberInRange(gridParams.gridAngle, 0, 359.99)) ||
      (gridParams.customGridElevationMode === 'Absolute' &&
        !isInputNumberInRange(gridParams.customGridElevation, 0, 10000)),
    [
      gridParams.customGridElevation,
      gridParams.customGridElevationMode,
      gridParams.flatTerrainHeight,
      gridParams.flatTerrainMode,
      gridParams.gridAngle,
      gridParams.gridHeight,
      gridParams.gridResolution,
    ]
  );
  useEffect(() => {
    if (!usesExternalPreview || activeStep !== 1) {
      onPreviewStateChange?.(null);
      return;
    }

    onPreviewStateChange({
      gridId: draftGridId ?? routeGridId,
      isPreviewLoading: disableAdvanceActions,
      onUpdatePreview:
        draftGridId && typeof onUpdatePreview === 'function' ? () => onUpdatePreview(String(draftGridId)) : null,
      hasValidationErrors: hasGridSensorPointsError || hasGridInputValidationError,
      mapCenter: gridSettingsMapCenter,
      gridParams,
    });
  }, [
    activeStep,
    disableAdvanceActions,
    draftGridId,
    gridParams,
    gridSettingsMapCenter,
    hasGridInputValidationError,
    hasGridSensorPointsError,
    onPreviewStateChange,
    onUpdatePreview,
    routeGridId,
    usesExternalPreview,
  ]);
  const numericVolumeLimit = useMemo(() => {
    const parsedLimit = Number(volumeLimit);
    return Number.isFinite(parsedLimit) ? parsedLimit : null;
  }, [volumeLimit]);
  const gridVolumePayload = useMemo(() => {
    const analysisArea = normalizeMultiPolygon<{ x: number; y: number; z?: number }>(
      gridParams.analysisAreas?.length ? gridParams.analysisAreas : gridParams.analysisArea
    ).filter((polygon) => polygon.length >= 3);
    const gridHeight = Number(gridParams.gridHeight);

    return analysisArea.length > 0 && Number.isFinite(gridHeight) && gridHeight > 0
      ? { analysisArea, gridHeight }
      : null;
  }, [gridParams.analysisArea, gridParams.analysisAreas, gridParams.gridHeight]);

  useEffect(() => {
    if (!gridVolumePayload) {
      setGridVolume(null);
      setIsGridVolumeLoading(false);
      setHasGridVolumeCalculationError(false);
      return;
    }

    let cancelled = false;
    setIsGridVolumeLoading(true);
    setHasGridVolumeCalculationError(false);
    calculateGridVolume(gridVolumePayload)
      .then(({ gridVolume: calculatedVolume }) => {
        if (!cancelled) setGridVolume(calculatedVolume);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error calculating grid volume:', error);
        setGridVolume(null);
        setHasGridVolumeCalculationError(true);
        viewContext.notification.show(
          'We could not calculate the grid volume. Please review the Analysis Area and Volume Height, then try again.',
          'error',
          true,
          'toast'
        );
      })
      .finally(() => {
        if (!cancelled) setIsGridVolumeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gridVolumePayload, setGridVolume]);

  const hasGridVolumeError = Boolean(
    gridVolume != null &&
    checkVolume({
      totalVolume: gridVolume,
      volumeLimit: numericVolumeLimit,
      gridHeight: Number(gridParams.gridHeight),
      gridResolution: Number(gridParams.gridResolution),
    })
  );
  const canProceedFromAnalysisStep = isReadOnlyReview
    ? true
    : Boolean(
        isGridNameValid &&
        isGridAnalysisAreaValid &&
        isGridCoordinateSystemValid &&
        gridVolume != null &&
        !isGridVolumeLoading &&
        !hasGridVolumeCalculationError &&
        !hasGridVolumeError &&
        !hasGridInputValidationError
      );
  const isNextDisabledByValidation = !canProceedFromAnalysisStep;
  const isNextDisabledByPolling = disableAdvanceActions;
  const isNextLoading = isAdvancingStep || isNextDisabledByPolling;

  const analysisVolumeView = (
    <Cadastral
      project={projectData}
      projectId={resolvedProjectId ?? undefined}
      readOnly={isReadOnlyReview}
      defaultGridName={draftGridId ? grid.name : defaultGridName}
      onBillingLinkClick={handleBillingLinkClick}
    />
  );

  const gridSettingsView = (
    <div className="grid-analysis-step">
      <ProjectCreationSplitLayout
        className={`grid-settings-step ${isReadOnlyReview ? 'grid-settings-step--review' : ''}`.trim()}
        leftClassName="grid-settings-step__sidebar"
        rightClassName={`grid-settings-step__preview grid-settings-step__preview--project${
          usesExternalPreview ? ' grid-settings-step__preview--external-host' : ''
        }`}
        left={
          <GridSettingsForm
            onGridResolutionChange={() => {
              isGridResolutionUserDefinedRef.current = true;
            }}
            onOpenGeodataSettings={handleOpenGeodataSettings}
            readOnly={isReadOnlyReview}
            showGeodataSettingsLink={!isStandaloneGridCreationFlow}
            disableGeodataSettingsLink={hasGridSensorPointsError || hasGridInputValidationError}
            onBillingLinkClick={handleBillingLinkClick}
          />
        }
        right={
          usesExternalPreview ? (
            <div ref={previewSlotRef} className="grid-settings-step__preview-slot" />
          ) : (
            <MapPreviewModeFrame
              gridId={draftGridId ?? routeGridId}
              isPreviewLoading={disableAdvanceActions}
              onUpdatePreview={
                draftGridId && typeof onUpdatePreview === 'function' ? () => onUpdatePreview(String(draftGridId)) : null
              }
              hasValidationErrors={hasGridSensorPointsError || hasGridInputValidationError}
            >
              <CadastralMapSection
                isReview={false}
                readOnly={true}
                allowAnalysisAreaAngleCopy={true}
                isParcelInteractionDisabled={true}
                mapType={gridSettingsMapType}
                mapViews={gridSettingsMapViews}
                setMapType={setGridSettingsMapType}
                setMapViews={setGridSettingsMapViews}
                mapCenter={gridSettingsMapCenter}
                crs2056={crs2056}
                gridParams={gridParams}
                Style={Style}
                legalBorderRef={legalBorderRef}
                buildableBorderRef={buildableBorderRef}
                analysisAreaRef={analysisAreaRef}
                legalBorderInactiveRefs={legalBorderInactiveRefs}
                buildableBorderInactiveRefs={buildableBorderInactiveRefs}
                analysisAreaInactiveRefs={analysisAreaInactiveRefs}
                showInactiveNonAnalysisBorders
                showOnlyAnalysisAreas
                inactiveAnalysisAreaStyle={Style.analysisArea}
                activeAnalysisAreaIndex={gridParams.activeAnalysisAreaIndex}
                isDrawingRef={isDrawingRef}
                showEditableCoordinatesArea={false}
                AnalysisAreaCenterMarkerSync={ReadOnlyAnalysisAreaCenterMarkerSync}
                cadastralOpacity={Number(Settings[import.meta.env.MODE as keyof typeof Settings].cadastral_opacity)}
                focusedPolygonRef={null}
                coordinateSystem={defaultCoordinateSystemPreference}
                cadastralLayerKey={0}
              />
            </MapPreviewModeFrame>
          )
        }
      />
    </div>
  );

  if (!resolvedProjectId) {
    return (
      <div className="review-loader-center">
        <UMLoader />
      </div>
    );
  }

  return (
    <ProjectCreationShell
      title={breadcrumbTitle ?? (activeStep === 0 ? 'Analysis Volume' : 'Grid Settings')}
      onClose={handleCloseClick}
      hideDefaultHeader={isReadOnlyReview && activeStep === 0}
      customHeader={
        isReadOnlyReview && activeStep === 0 ? (
          <ReviewHeader
            name={grid?.name}
            description={grid?.description}
            sizeLabel={gridSizeLabel}
            showSize
            item={grid ?? {}}
            onEditDescription={(value) => {
              setGrid((prev) => ({ ...prev, description: value }));
            }}
            onSaveDescription={(value) => updateGrid(String(resolvedReviewGridId), { description: value })}
            onClose={handleExit}
          />
        ) : null
      }
      footerLeft={
        activeStep > 0 ? (
          <Button
            variant="contained"
            startIcon={<ChevronLeftIcon />}
            onClick={() => setActiveStep(0)}
            className="onboarding-prev-button"
            disabled={hasGridSensorPointsError || hasGridInputValidationError}
          >
            Back
          </Button>
        ) : null
      }
      footerRight={
        activeStep === 0 ? (
          <UMButton
            text="NEXT"
            onClick={handleNextStep}
            icon={ArrowRight}
            customClassName="onboarding-finish-button"
            loading={isNextLoading}
            disabled={isNextDisabledByValidation || isAdvancingStep || isNextDisabledByPolling}
          />
        ) : isReadOnlyReview ? null : isStandaloneGridCreationFlow ? (
          <UMButton
            text={isCreatingStandaloneGrid ? 'CREATING...' : 'CREATE'}
            onClick={handleCreateStandaloneGrid}
            icon={ArrowRight}
            customClassName="onboarding-finish-button onboarding-finish-button--primary"
            disabled={isCreatingStandaloneGrid || hasGridSensorPointsError || hasGridInputValidationError}
            loading={isCreatingStandaloneGrid}
          />
        ) : (
          <UMButton
            text={isOpeningSimulationSettings ? 'OPENING...' : 'ADD SIM'}
            onClick={handleOpenSimulationSettings}
            icon={ArrowRight}
            customClassName="onboarding-finish-button onboarding-finish-button--primary"
            disabled={
              isOpeningSimulationSettings ||
              disableAdvanceActions ||
              hasGridSensorPointsError ||
              hasGridInputValidationError
            }
            loading={isOpeningSimulationSettings || disableAdvanceActions}
          />
        )
      }
    >
      {activeStep === 0 ? analysisVolumeView : gridSettingsView}

      <ExitDialog
        open={exitModalOpen}
        onClose={() => {
          setExitModalOpen(false);
          setPendingNavigateTo(null);
        }}
        mode="confirm"
        onCancel={() => {
          setExitModalOpen(false);
          setPendingNavigateTo(null);
        }}
        onConfirm={() => {
          setExitModalOpen(false);
          if (pendingNavigateTo) {
            const nextPath = pendingNavigateTo;
            setPendingNavigateTo(null);
            navigate(nextPath);
            return;
          }
          handleExit();
        }}
      />
    </ProjectCreationShell>
  );
};

export default AddGrid;
